import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Alert from "../../../components/alert";
import Button from "../../../components/button";
import Table, { TableColumns } from "../../../components/table";
import {
  useGetRunQuery,
  useLazyGetRunArtifactDownloadUrlQuery,
} from "../../../actions/policy-simulation.action";
import { RoutePaths } from "../../../models";
import {
  PolicySimulationDebugPayloadVersion,
  PolicySimulationProgress,
} from "../../../models/policySimulation";
import useScreenName from "../../../hooks/useScreenName";

function queryErrorMessage(error: unknown) {
  if (error && typeof error === "object" && "data" in error) {
    const data = error.data;
    if (typeof data === "string") return data;
    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof data.message === "string"
    ) {
      return data.message;
    }
  }
  return "Failed to load simulation run. Please refresh.";
}
function progressLabel(progress?: PolicySimulationProgress | null) {
  if (!progress) return "-";
  const rows =
    progress.processed_rows === undefined
      ? ""
      : ` (${progress.processed_rows} rows)`;
  return `${progress.step}: ${progress.status}${rows}`;
}

export default function PolicySimulationRunDetailPage() {
  useScreenName("Policy Simulation Run");
  const routeParams = useParams() as { runId?: string };
  const runId = routeParams.runId || "";
  const history = useHistory();
  const [error, setError] = useState("");
  const [rowNo, setRowNo] = useState("1");
  const [payloadVersion, setPayloadVersion] =
    useState<PolicySimulationDebugPayloadVersion>("effective");

  const {
    data: run,
    error: runError,
    isError: isRunError,
    refetch,
  } = useGetRunQuery(runId, {
    pollingInterval: 0,
    skip: !runId,
  });
  const [triggerArtifactUrl] = useLazyGetRunArtifactDownloadUrlQuery();

  async function download(artifact: "jsonl" | "csv") {
    try {
      const { url } = await triggerArtifactUrl({ runId, artifact }).unwrap();
      if (!url) throw new Error("Download URL is not available");
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Download is not ready");
    }
  }

  function debug() {
    const row = Number(rowNo);
    if (!Number.isInteger(row) || row < 1) {
      setError("Row number must be a positive integer");
      return;
    }
    history.push(
      `${RoutePaths.UNDERWRITING_UI_POLICY_SIMULATE}?runId=${encodeURIComponent(runId)}&rowNo=${row}&payloadVersion=${payloadVersion}`,
    );
  }

  const columns: TableColumns[] = [
    { field: "step", label: "Step" },
    { field: "job_name", label: "Job" },
    { field: "status", label: "Status" },
    { field: "status_reason", label: "Reason" },
  ];
  const jobs = run?.jobs || [];
  const rows = jobs.map((job) => ({
    ...job,
    status_reason: job.status_reason || "-",
  }));

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button
            variant="blue-text"
            onClick={() =>
              history.push(RoutePaths.CREDIT_DECISION_POLICY_SIMULATIONS)
            }
          >
            &larr; Back
          </Button>
          <h2 className="d-inline ml-3">Simulation run</h2>
        </div>
        <Button variant="blue-outline" onClick={refetch}>
          Refresh
        </Button>
      </div>
      {error && <Alert type="danger" message={error} />}
      {isRunError && (
        <Alert type="danger" message={queryErrorMessage(runError)} />
      )}
      {!run && !isRunError && <p className="text-muted">Loading...</p>}
      {run && (
        <>
          <div className="dashboard-container mb-4">
            <p>
              <strong>Run ID:</strong> {run.run_id}
            </p>
            <p>
              <strong>Mode:</strong> {run.mode}
            </p>
            <p>
              <strong>Status:</strong> {run.status}
            </p>
            <p>
              <strong>Description:</strong> {run.description || "-"}
            </p>
            <p>
              <strong>Actor:</strong> {run.actor_email || "-"}
            </p>
            <p>
              <strong>Progress:</strong> {progressLabel(run.progress)}
            </p>
            <details className="mb-3">
              <summary>Override specification</summary>
              <pre className="small bg-light p-3 mt-2">
                {JSON.stringify(run.override_spec, null, 2)}
              </pre>
            </details>
            <p>
              <strong>CSV include:</strong>{" "}
              {run.csv_include_fields?.join(", ") || "-"}
            </p>
            <p>
              <strong>CSV exclude:</strong>{" "}
              {run.csv_exclude_fields?.join(", ") || "-"}
            </p>
            {run.status_reason && (
              <p className="text-danger">{run.status_reason}</p>
            )}
            <Button
              disabled={!run.jsonl_artifact.available}
              onClick={() => download("jsonl")}
            >
              Download JSONL
            </Button>{" "}
            <Button
              disabled={!run.csv_artifact.available}
              onClick={() => download("csv")}
            >
              Download CSV
            </Button>
          </div>
          <div className="dashboard-container mb-4">
            <h5>Jobs</h5>
            <Table columns={columns} data={rows} keyField="job_id" />
          </div>
          <div className="dashboard-container">
            <h5>Debug a row</h5>
            <div className="row">
              <div className="col-md-3">
                <label>
                  Row number
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    value={rowNo}
                    onChange={(e) => setRowNo(e.target.value)}
                  />
                </label>
              </div>
              <div className="col-md-3">
                <label>
                  Payload
                  <select
                    className="form-control"
                    value={payloadVersion}
                    onChange={(e) =>
                      setPayloadVersion(
                        e.target.value as PolicySimulationDebugPayloadVersion,
                      )
                    }
                  >
                    <option value="effective">Effective</option>
                    <option value="original">Original</option>
                  </select>
                </label>
              </div>
            </div>
            <Button onClick={debug}>Open in policy simulator</Button>
          </div>
        </>
      )}
    </div>
  );
}
